<template>
  <div>
    <PageHeader :items="items">
      <template #buttons>
        <b-button
          variant="primary"
          @click="openCreateModal"
          class="d-flex align-items-center gap-1"
          v-if="$can(`${$route.meta.permission}.create`)"
        >
          <feather-icon icon="PlusIcon" size="12" />
          <span>{{ $t("createMaterial") }}</span>
        </b-button>
      </template>
    </PageHeader>
    <div class="row">
      <div 
        v-for="material in materials" 
        :key="material.id"
        class="col-lg-4 col-md-6 col-sm-12 mb-2"
      >
        <b-card class="h-100">
          <b-card-header class="d-flex justify-content-between align-items-center flex-wrap">
            <h4 class="mb-0 material-title">{{ getMaterialName(material) }}</h4>
            <div class="d-flex gap-1">
              <b-button
                variant="link"
                size="sm"
                @click="openEditModal(material)"
                v-if="$can(`${$route.meta.permission}.edit`)"
                class="p-0"
              >
              <feather-icon size="16" icon="Edit2Icon" />
              </b-button>
              <b-button
                variant="link"
                size="sm"
                @click="confirmDelete(material)"
                v-if="$can(`${$route.meta.permission}.delete`) && !material.is_default"
                class="p-0 text-danger"
              >
                <feather-icon icon="TrashIcon" size="16" />
              </b-button>
              <b-badge
                v-if="material.is_default_shown"
                variant="success"
                class="ml-1"
              >
                {{ $t("default") }}
              </b-badge>
            </div>
          </b-card-header>
          <b-card-body class="p-0">
            <div class="table-responsive materials-table-responsive">
              <table class="table table-sm table-bordered mb-0 materials-table">
                <thead>
                  <tr>
                    <th class="text-nowrap">{{ $t('parameter') }}</th>
                    <th class="text-nowrap text-center">{{ $t('defaultValue') }}</th>
                    <th class="text-nowrap text-center">{{ $t('minValue') }}</th>
                    <th class="text-nowrap text-center">{{ $t('maxValue') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr 
                    v-for="parameter in getOrderedParameters(material.parameters)" 
                    :key="parameter.id"
                  >
                    <td class="text-nowrap">{{ $t(parameter.name) }}</td>
                    <td class="text-center">{{ formatParameterValue(parameter, 'defaultValue') }}</td>
                    <td class="text-center">{{ formatParameterValue(parameter, 'minValue') }}</td>
                    <td class="text-center">{{ formatParameterValue(parameter, 'maxValue') }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </b-card-body>
        </b-card>
      </div>
    </div>

    <MaterialModal
      ref="materialModal"
      :show="showModal"
      :material="selectedMaterial"
      @saved="handleSaved"
      @close="closeModal"
    />
  </div>
</template>

<script>
import PageHeader from "@/components/PageHeader.vue";
import { mapGetters } from "vuex";
import MaterialModal from "./components/MaterialModal.vue";
import materialNameMixin from "@/mixins/materialNameMixin";

export default {
  components: {
    PageHeader,
    MaterialModal,
  },
  mixins: [materialNameMixin],
  computed: {
    ...mapGetters("materialsConfiguration", ["materials", "count"]),
    items() {
      return [
        {
          text: "Dental Twin",
          to: "/home",
        },
        {
          text: this.$t("settings"),
          to: "/settings",
        },
        {
          text: this.$t("materialsConfiguration"),
          active: true,
        },
      ];
    },
  },
  data() {
    return {
      showModal: false,
      selectedMaterial: null,
    };
  },
  async mounted() {
    await this.loadMaterials();
  },
  methods: {
    formatParameterValue(parameter, field) {
      const value = parameter[field];
      if (value == null) return "";
      const normalize = (name) =>
        String(name || "")
          .trim()
          .replace(/([a-z])([A-Z])/g, "$1 $2")
          .replace(/[_-]+/g, " ")
          .toLowerCase();
      const name = normalize(parameter.name);
      const isThreeDecimalParameter = [
        "cement gap",
        "occlusal contact strength",
        "proximal contact strength",
        "gingival contact strength",
      ].includes(name);
      return Number(value).toFixed(isThreeDecimalParameter ? 3 : 2);
    },
    getOrderedParameters(parameters) {
      const order = [
        "cement gap",
        "cement gap start",
        "border thickness",
        "minimum thickness",
        "milling head diameter",
        "occlusal contact strength",
        "proximal contact strength",
        "gingival contact strength",
      ];
      const normalize = (name) =>
        String(name || "")
          .trim()
          .replace(/([a-z])([A-Z])/g, "$1 $2")
          .replace(/[_-]+/g, " ")
          .toLowerCase();

      const indexMap = order.reduce((acc, key, idx) => {
        acc[key] = idx;
        return acc;
      }, {});

      return (parameters || [])
        .map((p, idx) => ({
          p,
          idx,
          orderIdx: indexMap[normalize(p?.name)] ?? 999,
        }))
        .sort((a, b) => (a.orderIdx - b.orderIdx) || (a.idx - b.idx))
        .map((x) => x.p);
    },
    async loadMaterials() {
      try {
        this.$store.commit("showLoader", true);
        await this.$store.dispatch("materialsConfiguration/list");
      } catch (e) {
        console.error("api error ___", e);
      } finally {
        this.$store.commit("showLoader", false);
      }
    },
    openCreateModal() {
      this.selectedMaterial = null;
      this.showModal = true;
    },
    openEditModal(material) {
      this.selectedMaterial = material;
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
      this.selectedMaterial = null;
    },
    async handleSaved() {
      await this.loadMaterials();
    },
    confirmDelete(material) {
      this.$bvModal
        .msgBoxConfirm(
          this.$t("doYouWantToDeleteThisRecord"),
          {
            title: this.$t("delete"),
            size: "sm",
            buttonSize: "sm",
            okVariant: "danger",
            okTitle: this.$t("yesDeleteIt"),
            cancelTitle: this.$t("cancel"),
            footerClass: "p-2",
            hideHeaderClose: false,
            centered: true,
          }
        )
        .then((value) => {
          if (value) {
            this.deleteMaterial(material);
          }
        });
    },
    async deleteMaterial(material) {
      try {
        this.$store.commit("showLoader", true);
        await this.$store.dispatch("materialsConfiguration/destroy", material.id);
        await this.loadMaterials();
      } catch (e) {
        console.error("api error ___", e);
      } finally {
        this.$store.commit("showLoader", false);
      }
    },
  },
};
</script>

<style scoped lang="scss" src="./Index.scss"></style>